import { useState, useEffect } from 'react'
import { articlesApi, type Article } from '../utils/api'
import ArticleEditor from '../components/ArticleEditor'

type Toast = { type: 'success' | 'error'; message: string } | null

export default function Articles() {
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [editingArticle, setEditingArticle] = useState<Article | undefined>(undefined)
  const [isCreating, setIsCreating] = useState(false)
  const [toast, setToast] = useState<Toast>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  async function loadArticles() {
    setLoading(true)
    try {
      const { articles: list } = await articlesApi.list()
      setArticles(list)
    } catch (err) {
      showToast('error', `Failed to load articles: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadArticles() }, [])

  async function handleDelete(article: Article) {
    if (!confirm(`Delete "${article.title}"? This cannot be undone.`)) return
    setDeletingId(article.id)
    try {
      await articlesApi.delete(article.id)
      showToast('success', `✓ "${article.title}" deleted`)
      await loadArticles()
    } catch (err) {
      showToast('error', `Delete failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeletingId(null)
    }
  }

  function handleSaved() {
    setEditingArticle(undefined)
    setIsCreating(false)
    loadArticles()
  }

  // ── Editor View ────────────────────────────────────────────────────────────
  if (isCreating || editingArticle) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <ArticleEditor
          article={editingArticle}
          onSaved={handleSaved}
          onCancel={() => { setEditingArticle(undefined); setIsCreating(false) }}
        />
      </div>
    )
  }

  const filtered = articles.filter(
    (a) =>
      a.title.toLowerCase().includes(filter.toLowerCase()) ||
      a.slug.toLowerCase().includes(filter.toLowerCase()) ||
      (a.category ?? '').toLowerCase().includes(filter.toLowerCase())
  )

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '1.4rem' }}>Content Manager</h1>
          <p style={{ fontSize: '0.82rem', marginTop: 4 }}>
            {articles.length} article{articles.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          id="btn-new-article"
          className="btn btn-primary"
          onClick={() => setIsCreating(true)}
        >
          + New Article
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          id="input-article-search"
          className="form-input"
          placeholder="🔍 Search by title, slug, or category…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* Table */}
      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 44 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 24px' }}>
            <div className="empty-state-icon">{filter ? '🔍' : '📝'}</div>
            <p>{filter ? 'No articles match your search' : 'No articles yet. Create your first one!'}</p>
            {!filter && (
              <button className="btn btn-primary" onClick={() => setIsCreating(true)}>
                + New Article
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Category</th>
                <th>Status</th>
                <th>Updated</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((article) => (
                <tr key={article.id}>
                  <td>
                    <span
                      style={{ color: 'var(--text-primary)', fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => setEditingArticle(article)}
                    >
                      {article.title}
                    </span>
                  </td>
                  <td>
                    <code style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>/{article.slug}</code>
                  </td>
                  <td>
                    {article.category ? (
                      <span className="badge badge-accent">{article.category}</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${article.is_published ? 'badge-success' : 'badge-warning'}`}>
                      {article.is_published ? '✓ Published' : '○ Draft'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {new Date(article.updated_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        id={`btn-edit-${article.id}`}
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditingArticle(article)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        id={`btn-delete-article-${article.id}`}
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(article)}
                        disabled={deletingId === article.id}
                      >
                        {deletingId === article.id ? '…' : '🗑'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  )
}
